import { getProjectName } from 'lib/get-project-name'
import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Cell, Row } from 'ui/grid'
import { NumberInput } from 'ui/number-input'
import { useRangeValue } from 'ui/range-slider/useRangeValue'

import { useAppConfig } from '../../application/configuration'
import { Slider } from './Slider'

type RangeValue = {
  from: number
  to: number
}
export type Props = {
  min: number
  max: number
  step?: number
  value?: RangeValue
  onChange?: (value: RangeValue) => void
  onSubmit?: (value: RangeValue) => void
  single?: boolean
  inputSize?: 'medium' | 'small'
  name?: string
}

export const RangeSlider: FC<Props> = ({
  min,
  max,
  step = 1,
  value = { from: min, to: max },
  onChange,
  onSubmit,
  inputSize = 'medium',
  single = false,
  name
}) => {
  const [rangeValue, setRangeValue] = useRangeValue({
    min,
    max,
    step,
    value
  })

  const [submitAwaits, setSubmitAwaits] = useState(false)

  useEffect(() => {
    if (submitAwaits) {
      onSubmit && onSubmit(rangeValue)
      setSubmitAwaits(false)
    }
  }, [submitAwaits])

  useEffect(() => {
    onChange && onChange(rangeValue)
  }, [onChange, rangeValue, rangeValue.from, rangeValue.to])

  const i18n = useAppConfig().i18n

  const [inputValues, setInputValues] = useState(rangeValue)

  const commitInputValues = () => {
    setRangeValue(inputValues)
    setSubmitAwaits(true)
  }

  useEffect(() => {
    setInputValues(rangeValue)
  }, [rangeValue])

  useEffect(() => {
    setRangeValue({ from: value.from, to: value.to })
  }, [setRangeValue, value.from, value.to])

  return (
    <>
      <Fields inputSize={inputSize}>
        <Cell size={6}>
          <NumberInput
            block
            formatter={(value) => (name === 'price' ? value.toLocaleString(i18n.locale) : String(value))}
            label={single ? null : getProjectName() === 'generic_v2' ? 'от' : 'От'}
            size={inputSize}
            value={inputValues.from.toFixed(1)}
            onChange={(value) => setInputValues({ from: value, to: inputValues.to })}
            onEnter={commitInputValues}
            onBlur={commitInputValues}
            name={name}
          />
        </Cell>
        {!single && (
          <Cell size={6}>
            <NumberInput
              formatter={(value) => (name === 'price' ? value.toLocaleString(i18n.locale) : String(value))}
              block
              value={inputValues.to.toFixed(1)}
              label={getProjectName() === 'generic_v2' ? 'до' : 'До'}
              onChange={(value) => setInputValues({ from: inputValues.from, to: value })}
              onEnter={commitInputValues}
              onBlur={commitInputValues}
              name={name}
            />
          </Cell>
        )}
      </Fields>

      <Slider
        value={rangeValue}
        onChange={setRangeValue}
        min={min}
        max={max}
        onChangeEnd={() => onSubmit && onSubmit(rangeValue)}
        single={single}
      />
    </>
  )
}

const Fields = styled(Row)<{ inputSize: 'medium' | 'small' }>`
  margin-bottom: ${({ inputSize }) => (inputSize === 'medium' ? '2.4rem' : '1.2rem')};

  ${() =>
    getProjectName() === 'generic_v2' &&
    `
        padding-left: 4px;
        padding-right: 4px;
      `}
`
